import { useNavigate, useParams } from "react-router-dom";
import { useGlobalData } from "../components/GlobalDataProvider";
import { ModuleStatuses, ModuleTypeFamily, ModuleTypeId } from "../types/DBTables";
import { DTOModule } from "../types/building";
import { Card, ListGroup, ListGroupItem } from "react-bootstrap";
import ModuleConfigurator from "../components/Configuration/ModuleConfigurator";
import { Colors } from "../modules/Colors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCog, faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import StickyBreadCrumb from "../components/Global/StickyBreadCrumb";
import { useEffect } from "react";

class HeaderViewModel {
  constructor(public title: string, public modules: DTOModule[], public expandModuleId: number | undefined) {}

  allModulesConfigured() {
    return !this.modules.find((m) => m.status !== ModuleStatuses.Configured);
  }
}

const excludedModuleTypes = [
  ModuleTypeId.Lokaler,
  ModuleTypeId.KylaAnnatSystem,
  ModuleTypeId.KylaSaknas,
  ModuleTypeId.VarmePrimarAnnatSystem,
  ModuleTypeId.VarmePrimarSaknas,
  ModuleTypeId.VarmeSekundarSaknas,
];

const Configuration = () => {
  const { buildingId, family, moduleId } = useParams();
  const navigate = useNavigate();
  const globalData = useGlobalData();

  const building = buildingId ? globalData.buildings?.find((b) => b.id === parseInt(buildingId)) : undefined;

  const modules = building?.modules || [];

  //Sort the modules depeding on the order of the MoudleTypeFamily Basdata, Ventilation, VärmePrimar, VärmeSekundar, VärmeEl, Kyla
  //Function that takes familyID and returns a number that is used to sort the headers
  const familySortOrder = (family: ModuleTypeFamily) => {
    switch (family) {
      case ModuleTypeFamily.Basdata:
        return 1;
      case ModuleTypeFamily.Ventilation:
        return 2;
      case ModuleTypeFamily.VärmePrimar:
        return 3;
      case ModuleTypeFamily.VärmeSekundar:
        return 4;
      case ModuleTypeFamily.VärmeEl:
        return 5;
      case ModuleTypeFamily.Kyla:
        return 6;
      default:
        return 7;
    }
  };

  const allFamilies = modules
    .filter((m) => !excludedModuleTypes.includes(m.moduleTypeId))
    .reduce((acc, cur) => {
      let f = globalData.moduleTypes.find((mt) => mt.id === cur.moduleTypeId)?.family;
      if (f) {
        if (!acc.find((existing) => existing === f)) acc.push(f);
      }
      return acc;
    }, [] as ModuleTypeFamily[])
    .sort((a, b) => {
      return familySortOrder(a) - familySortOrder(b);
    });

  const headers: HeaderViewModel[] = [];

  const getFamilyTitel = (family: ModuleTypeFamily) => {
    switch (family) {
      case ModuleTypeFamily.Basdata:
        return "Basdata";
      case ModuleTypeFamily.VärmePrimar:
        return "Primärvärmesystem";
      case ModuleTypeFamily.VärmeSekundar:
        return "Sekundärvärmesystem";
      case ModuleTypeFamily.VärmeEl:
        return "Värmesystem el";
      case ModuleTypeFamily.Kyla:
        return "Kyla";
      case ModuleTypeFamily.Ventilation:
        return "Ventilation";
      default:
        return "?";
    }
  };

  let expandedModule: DTOModule | undefined = undefined;
  for (var i = 0; i < allFamilies.length; i++) {
    const family = allFamilies[i];
    const title = getFamilyTitel(family);

    const modulesUnderHeader = modules.filter(
      (m) => !excludedModuleTypes.includes(m.moduleTypeId) && globalData.moduleTypes.find((mt) => mt.id === m.moduleTypeId)?.family === family
    );
    const locExpandedModule = moduleId ? modulesUnderHeader.find((m) => m.id === Number(moduleId)) : undefined;
    if (locExpandedModule) expandedModule = locExpandedModule;
    headers.push(new HeaderViewModel(title, modulesUnderHeader, locExpandedModule?.id));

    //Sort the modules depeding on the order of the MoudleTypeFamily Basdata, Ventilation, VärmePrimar, VärmeSekundar, VärmeEl, Kyla
    //Function that takes familyID and returns a number that is used to sort the headers
  }

  const selectFamily = (family: string) => {
    navigate("/Buildings/" + buildingId + "/Configuration/" + family);
  };

  const selectModule = (m: DTOModule) => {
    navigate("/Buildings/" + buildingId + "/Configuration/" + family + "/" + m.id.toString());
  };

  const onBack = () => {
    navigate("..");
  };

  useEffect(() => {
    if (!family && !moduleId) {
      //Find a good family to start expanded
      const firstFamilyWithUnConfiguredModules = headers.find((h) => h.modules.find((m) => m.status !== ModuleStatuses.Configured));
      const navigateTo = firstFamilyWithUnConfiguredModules
        ? "/Buildings/" + buildingId + "/Configuration/" + firstFamilyWithUnConfiguredModules.title
        : "/Buildings/" + buildingId + "/Configuration/" + headers[0].title;
      navigate(navigateTo);
    }
  });

  const breadcrumbItems: any[] = [];

  const buildingInfo = building?.modules.find((m) => m.moduleTypeId === ModuleTypeId.Byggnadsdata)?.data;
  const buildingData = buildingInfo ? JSON.parse(buildingInfo) : undefined;
  const område = buildingData?.område as string;

  breadcrumbItems.push({ text: område + " - " + building?.name, navTo: "/Buildings/" + buildingId, icon: faCog });
  if (family) breadcrumbItems.push({ text: family, navTo: "/Buildings/" + buildingId + "/Configuration/" + family });
  if (expandedModule) breadcrumbItems.push({ text: expandedModule.name, navTo: "/Buildings/" + buildingId + "/Configuration/" + family + "/" + expandedModule.id.toString() });

  breadcrumbItems[breadcrumbItems.length - 1].active = true;

  return (
    <>
      <StickyBreadCrumb items={breadcrumbItems} />
      {headers.map((h) => (
        <div key={h.title}>
          <Card className="mt-1">
            <Card.Header role="button" style={{ backgroundColor: Colors.GrayLight }} onClick={() => selectFamily(h.title)}>
              <h5>
                {h.allModulesConfigured() ? (
                  <FontAwesomeIcon icon={faCircleCheck} className="me-3" color={Colors.GreenDark} />
                ) : (
                  <FontAwesomeIcon icon={faExclamationCircle} className="me-3" title="Ej konfigurerad" color={Colors.RedMedium} />
                )}

                {h.title}
              </h5>
            </Card.Header>
            {h.title === family && (
              <>
                {h.expandModuleId ? (
                  <>
                    <ModuleConfigurator />
                  </>
                ) : (
                  <ListGroup className="list-group-flush">
                    {h.modules.map((m) => (
                      <ListGroupItem role="button" key={m.id} onClick={() => selectModule(m)}>
                        {m.status === ModuleStatuses.Configured ? (
                          <FontAwesomeIcon icon={faCircleCheck} className="me-3" color={Colors.GreenDark} />
                        ) : (
                          <FontAwesomeIcon icon={faExclamationCircle} className="me-3" title="Ej konfigurerad" color={Colors.RedMedium} />
                        )}
                        {m.name}
                      </ListGroupItem>
                    ))}
                  </ListGroup>
                )}
              </>
            )}
          </Card>
        </div>
      ))}

      <button type="button" className="btn btn-primary me-2 float-end mt-3" onClick={onBack}>
        Tillbaka
      </button>
    </>
  );
};

export default Configuration;
